import React from "react";
import PropTypes from "prop-types";
import {
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox
} from "@material-ui/core";
import { Lens as LensIcon } from "@material-ui/icons";
import { green, red, yellow } from "@material-ui/core/colors";
import { makeStyles } from "@material-ui/core/styles";
import HelperTooltip from "../Shared/HelperTooltip/HelperTooltip";

const useStyles = makeStyles((theme) => ({
  div: {
    display: "flex",
    alignItems: "center"
  },
  p: {
    display: "flex",
    alignItems: "center"
  },
  visuallyHidden: {
    display: "none"
  }
}));

function EnhancedTableHead(props) {
  const classes = useStyles();
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const headCells = [
    {
      id: "status",
      numeric: false,
      disablePadding: false,
      padding: "6px 12px 6px 12px",
      label: (
        <div className={classes.div}>
          Status
          <HelperTooltip
            tooltipText={
              <>
                <p className={classes.p}>
                  <LensIcon style={{ color: red[300], fontSize: 16 }} />: Not
                  Submitted
                </p>
                <p className={classes.p}>
                  <LensIcon style={{ color: yellow[300], fontSize: 16 }} />:
                  Default Creative Assigned
                </p>
                <p className={classes.p}>
                  <LensIcon style={{ color: green[300], fontSize: 16 }} />:
                  Submitted
                </p>
              </>
            }
          />
        </div>
      )
    },
    {
      id: "data",
      numeric: false,
      disablePadding: false,
      padding: "6px 12px 6px 12px",
      label: (
        <div className={classes.div}>
          Assigned Creatives
          <HelperTooltip tooltipText="Click icon to see creatives currently assigned to your placement" />
        </div>
      )
    },
    { id: "siteId", numeric: false, disablePadding: false, label: "Site" },
    {
      id: "placement",
      numeric: false,
      disablePadding: false,
      label: "Placement"
    },
    {
      id: "name",
      numeric: true,
      disablePadding: false,
      label: "ID"
    },
    {
      id: "pricingSchedule.startDate",
      numeric: false,
      disablePadding: false,
      label: "Start"
    },
    {
      id: "pricingSchedule.endDate",
      numeric: false,
      disablePadding: false,
      label: "End"
    },
    {
      id: "days_till_start",
      numeric: false,
      disablePadding: false,
      padding: "6px 2px 6px 12px",
      label: (
        <div className={classes.div}>
          Lead Time
          <HelperTooltip tooltipText="Time remaining until placement start date" />
        </div>
      )
    },
    {
      id: "lastModifiedInfo.time",
      numeric: false,
      disablePadding: false,
      label: "Last Modified"
    },
    {
      id: "size.width",
      numeric: false,
      disablePadding: false,
      label: "Dimensions"
    },
    {
      id: "compatibility.display",
      numeric: false,
      disablePadding: false,
      padding: "6px 2px 6px 12px",
      label: (
        <div className={classes.div}>
          Compatibility
          <HelperTooltip tooltipText="Which creatives and creative sets you can assign to your placement. Bart makes sure that all the creatives in your creative sets work in the placement environment." />
        </div>
      )
    },
    {
      id: "pricingSchedule.pricingType",
      numeric: false,
      disablePadding: false,
      padding: "6px 2px 6px 12px",
      label: (
        <div className={classes.div}>
          Serving Types
          <HelperTooltip
            tooltipText={
              <>
                <p>
                  Site Served: Placements utilizing 1x1 impression and click
                  pixels
                </p>
                <p>
                  3rd Party: Dynamic placements with ability to serve image,
                  HTML5 and RM creative
                </p>
              </>
            }
          />
        </div>
      )
    }
  ];

  return (
    <TableHead>
      <TableRow>
        <TableCell
          padding="checkbox"
          style={{
            backgroundColor: "white"
          }}
        >
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ "aria-label": "select all placements" }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            style={{
              padding: headCell.padding ? headCell.padding : null,
              backgroundColor: "white"
            }}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.id !== "status" &&
            headCell.id !== "data" &&
            headCell.id !== "placement" &&
            headCell.id !== "pricingSchedule.startDate" &&
            headCell.id !== "pricingSchedule.endDate" &&
            headCell.id !== "lastModifiedInfo.time" &&
            headCell.id !== "days_till_start" &&
            headCell.id !== "size.width" &&
            headCell.id !== "compatibility.display" &&
            headCell.id !== "pricingSchedule.pricingType" ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <span className={classes.visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </span>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired
};

export default EnhancedTableHead;
